import { Component, OnDestroy, OnInit, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, combineLatest, map, timer } from 'rxjs';
import { ReadyBoardDataService } from '../../core/ready-board/ready-board-data.service';
import { ReadyBoardConnectionService } from '../../core/ready-board/ready-board-connection.service';
import { ServerTimeService } from '../../core/server-time.service';
import { ScheduleItem } from '../../core/ready-board/ready-board.models';

@Component({
  selector: 'app-display',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './display.html',
  styleUrl: './display.scss'
})
export class DisplayComponent implements OnInit, OnDestroy {
  private readonly readyBoardData = inject(ReadyBoardDataService);
  private readonly connectionService = inject(ReadyBoardConnectionService);
  private readonly serverTime = inject(ServerTimeService);
  private readonly ngZone = inject(NgZone);

  private readonly timeFormatter = new Intl.DateTimeFormat('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest'
  });

  private readonly dateFormatter = new Intl.DateTimeFormat('ro-RO', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Bucharest'
  });

  // Clock timer runs outside Angular's Zone to avoid keeping the app permanently
  // unstable. 10s interval is enough since only HH:mm is displayed (no seconds).
  // serverTime.now() returns device time + server offset calibrated in ngOnInit.
  private reloadHandle: ReturnType<typeof setTimeout> | undefined;

  private readonly now$ = new Observable<Date>(observer => {
    const sub = this.ngZone.runOutsideAngular(() =>
      timer(0, 10000).pipe(map(() => this.serverTime.now())).subscribe({
        next: date => this.ngZone.run(() => observer.next(date)),
        error: e => this.ngZone.run(() => observer.error(e)),
        complete: () => this.ngZone.run(() => observer.complete()),
      })
    );
    return () => sub.unsubscribe();
  });

  async ngOnInit(): Promise<void> {
    await this.serverTime.calibrate();
    this.ngZone.runOutsideAngular(() => {
      const target = new Date();
      target.setHours(4, 0, 0, 0);
      if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
      this.reloadHandle = setTimeout(() => window.location.reload(), target.getTime() - Date.now());
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.reloadHandle);
  }

  readonly vm$ = combineLatest([
    this.readyBoardData.readyBoardState$,
    this.readyBoardData.source$,
    this.connectionService.indicator$,
    this.now$
  ]).pipe(
    map(([state, source, connectionIndicator, now]) => ({
      state,
      source,
      connectionIndicator,
      time: this.timeFormatter.format(now),
      date: this.dateFormatter.format(now),
      topPickups: state.activePickups.slice(0, 4),
      nextSchedule: findNextSchedule(state.schedule, now)
    }))
  );

  isNext(item: ScheduleItem, next: ScheduleItem | null): boolean {
    return !!next && item.time === next.time && item.label === next.label;
  }
}

function findNextSchedule(schedule: ScheduleItem[], now: Date): ScheduleItem | null {
  if (!schedule?.length) return null;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const entries = schedule
    .map(item => ({ item, minutes: toMinutes(item.time) }))
    .filter(entry => entry.minutes !== null) as { item: ScheduleItem; minutes: number }[];

  if (!entries.length) return null;
  entries.sort((a, b) => a.minutes - b.minutes);

  const next = entries.find(entry => entry.minutes >= nowMinutes);
  if (next) return next.item;
  return entries[entries.length - 1].item;
}

function toMinutes(time: string): number | null {
  const [hours, minutes] = (time ?? '').split(':').map(value => Number.parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}
